import { PaginateBankItem } from '@/fsd/feature/banks/paginate-bank-item';
import { useGetMutualSettlementsFilters } from '@/fsd/entities/mutualSettlements/model/hooks/useGetMutualSettlementsFilters';
import { usePagination } from '@/fsd/pages/banks/model/hooks/usePagination';
import { TitledTablePage } from '@/fsd/pages/layouts/titled-table-page';
import { useSort } from '@/fsd/shared/hooks/useSort';
import { IMutualSettlementsSortFields } from '@/fsd/entities/mutualSettlements/model/IMutualSettlementsSortFields';
import BodyTable from '@/fsd/shared/ui/BodyTable';
import EmptyData from '@/fsd/shared/ui/EmptyData/EmptyData';
import Loader from '@/fsd/shared/ui/Loader/Loader';
import Table from '@/fsd/shared/ui/Table';
import { observer } from 'mobx-react-lite';
import styles from './MutualSettlements.module.scss';
import MutualSettlementsFilters from '../MutualSettlementsFilters/MutualSettlementsFilters';
import MutualSettlementsTableHeader from '../MutualSettlementsTableHeader/MutualSettlementsTableHeader';
import MutualSettlementsBody from '../MutualSettlementsBody/MutualSettlementsBody';
import { getReconciliations } from '@/fsd/entities/mutualSettlements';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import MutualSettlementsModal from '../MutualSettlementsModal/MutualSettlementsModal';
import CreateReconciliationModal from '../CreateReconciliationModal/CreateReconciliationModal';

const MutualSettlements = observer(() => {
  const { page, pageSize, setPage, handlePageChange, handlePageSizeChange } =
    usePagination();
  const { sorted, handleSortChange } = useSort<IMutualSettlementsSortFields>({
    defaultFields: ['created_at'],
    defaultOrders: ['desc'],
  });

  const {
    filters,
    filterFields,
    handleFilterChange,
    handleFilterApply,
    activeFilters,
    setActiveFilters,
  } = useGetMutualSettlementsFilters({ setPage });

  const { data, isFetching: isLoading } = useQuery({
    queryKey: ['mutualSettlements', { page, pageSize, filters, sorted }],
    queryFn: () =>
      getReconciliations({ page, limit: pageSize, filters, sorted }),
    placeholderData: keepPreviousData,
  });

  return (
    <TitledTablePage title="Взаиморасчеты">
      <MutualSettlementsFilters
        activeFilters={activeFilters}
        filterFields={filterFields}
        handleFilterApply={handleFilterApply}
        handleFilterChange={handleFilterChange}
        filters={filters}
        setPage={setPage}
        setActiveFilters={setActiveFilters}
      />

      <div className={styles.mutualSettlementsTableScroll}>
        <Table tableClassName={styles.tableMutualSettlements}>
          <MutualSettlementsTableHeader
            sorted={sorted}
            handleSortChange={handleSortChange}
          />
          <BodyTable bodyClassName={styles.bodyTableMutualSettlements}>
            {isLoading ? (
              <EmptyData desc={<Loader />} />
            ) : !data?.data || data?.data?.length === 0 ? (
              <EmptyData />
            ) : (
              <MutualSettlementsBody state={data.data} />
            )}
          </BodyTable>
        </Table>
      </div>

      <PaginateBankItem
        page={page}
        totalPages={data?.meta.total_pages ?? 0}
        handlePageChange={handlePageChange}
        pageSize={pageSize}
        handlePageSizeChange={handlePageSizeChange}
      />
      <MutualSettlementsModal />
      <CreateReconciliationModal />
    </TitledTablePage>
  );
});

export default MutualSettlements;
